
import React, { Component, ReactNode } from 'react';
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  info: { componentStack: string } | null;
}

class RouteErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Log the error to an error reporting service
    console.error("Route Error Boundary caught an error:", error, info);
    this.setState({ info });
  }

  handleRefresh = () => {
    // Force reload the application
    window.location.reload();
  };

  handleGoHome = () => {
    // Navigate back to home page
    window.location.href = '/';
  };

  render() {
    const { children, fallback } = this.props;
    const { hasError, error } = this.state;

    if (hasError) {
      // Show custom fallback if provided, otherwise use default
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-muted/30">
          <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 border">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-center mb-2">Something went wrong</h2>
            <p className="text-muted-foreground text-center mb-6">
              {error?.message || "There was an error loading this page."}
            </p>
            
            {error?.stack && (
              <div className="mb-6 p-2 bg-muted rounded-md overflow-auto text-xs max-h-32">
                <pre>{error.stack}</pre>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={this.handleGoHome}>
                Go to Home Page
              </Button>
              <Button onClick={this.handleRefresh} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default RouteErrorBoundary;
